import React, { useState, useEffect } from 'react';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../ui/FreshnessDate';

const EfficiencyTimer = ({ isEmbedded = false }) => {
  const [activeTimer, setActiveTimer] = useState(null); // 'app' or 'zap'
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');

  useEffect(() => {
    let interval;
    if (activeTimer === 'app') {
      const messages = [
        "Finding app icon...",
        "Waiting to load...",
        "Logging in with FaceID...",
        "Navigating to '+' menu...",
        "Waiting for camera...",
        "Taking photo...",
        "Selecting Category...",
        "Typing amount...",
        "Waiting for upload..."
      ];
      setProgress(0);
      let ticks = 0;
      const totalTicks = 200; // 20 seconds at 100ms intervals
      interval = setInterval(() => {
        ticks++;
        setProgress((ticks / totalTicks) * 100);
        setStatusText(messages[Math.min(Math.floor((ticks / totalTicks) * messages.length), messages.length - 1)]);
        if (ticks >= totalTicks) {
          clearInterval(interval);
          setStatusText("Done. (20 seconds wasted)");
        }
      }, 100);
    } else if (activeTimer === 'zap') {
      const messages = [
        "Open WhatsApp",
        "Snap photo",
        "Hit Send"
      ];
      setProgress(0);
      let ticks = 0;
      const totalTicks = 50; // 5 seconds at 100ms intervals
      interval = setInterval(() => {
        ticks++;
        setProgress((ticks / totalTicks) * 100);
        setStatusText(messages[Math.min(Math.floor((ticks / totalTicks) * messages.length), messages.length - 1)]);
        if (ticks >= totalTicks) {
          clearInterval(interval);
          setStatusText("Photo sent via WhatsApp. Done. (5s)");
          trackEvent('tool_completion', { tool_name: 'efficiency_timer' });
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const startApp = () => setActiveTimer('app');
  const startZap = () => setActiveTimer('zap');

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to bypass accounting software data entry",
    "dateModified": getFreshnessDateIso(),
    "step": [
      { "@type": "HowToStep", "text": "Take a photo of a receipt via the WhatsApp camera." },
      { "@type": "HowToStep", "text": "Hit send to deliver it to Zap Expenses." },
      { "@type": "HowToStep", "text": "Zap AI extracts the vendor, amount, and date without logging into a dashboard." }
    ]
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl my-12 text-center">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <title>Receipt Scanning Speed Test | Zap Expenses</title>
      </Helmet>


      <FreshnessDate className="mb-4" />
      <div className="mb-6 border-b border-white/5 pb-4">
        <h1 className="text-4xl font-extrabold text-white mb-4">The Efficiency Test 2026</h1>

        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-4 shadow-lg">
          <p className="text-xl font-medium leading-relaxed mb-4">
            <strong>"Manual invoice processing costs UK SMEs an average of £12 to £15 per transaction in staff time and error correction."</strong>
          </p>
          <div className="text-[10px] text-indigo-300 italic border-t border-indigo-500/20 pt-2">
            Source: <a href="https://taxformed.com/blog/hidden-cost-manual-invoice-processing-uk-sme" target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-400">Taxformed, The Hidden Cost of Manual Invoice Processing</a>
          </div>
        </div>

        <div id="stat-leakage-2026" className="mb-6 bg-black/40 border border-slate-800 p-6 rounded-xl shadow-inner relative overflow-hidden text-center md:text-left">
          <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">Workflow Bottlenecks</p>
          <p className="text-2xl font-extrabold text-white mb-2">The Unclaimed Expense Crisis</p>
          <p className="text-slate-400 text-sm leading-relaxed">
            "UK sole traders leave £962m in expenses unclaimed annually, with 33% citing 'lost receipts' as the primary barrier to claiming."
          </p>
          <div className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-2 text-center md:text-left">
            Source: <a href="https://www.gosimpletax.com/insights/uk-expense-statistics/" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">GoSimpleTax, UK Expense Statistics</a>
          </div>
        </div>

        <div className="prose prose-invert max-w-none text-slate-300 md:text-center mx-auto mb-2">
          <p>
            The barrier to <strong>Making Tax Digital</strong> compliance is friction. Every second wasted waiting for an app to load, logging in with FaceID, and manually categorizing expenses represents operational leakage. Test the speed difference below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="bg-black/20 p-6 rounded-2xl border border-white/5">
          <button
            onClick={startApp}
            className="w-full py-4 mb-6 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold transition-all border border-slate-600 shadow-lg"
          >
            Launch "The App Way"
          </button>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-red-500 transition-all duration-100 ease-linear" style={{ width: `${activeTimer === 'app' ? progress : 0}%` }}></div>
          </div>
          <p className="text-xs text-slate-400 font-mono min-h-[1.5rem]">{activeTimer === 'app' ? statusText : 'Ready...'}</p>
        </div>

        <div className="bg-indigo-500/10 p-6 rounded-2xl border border-indigo-500/30">
          <button
            onClick={startZap}
            className="w-full py-4 mb-6 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-all shadow-lg shadow-emerald-500/20"
          >
            Launch "The Zap Way"
          </button>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-emerald-500 transition-all duration-100 ease-linear" style={{ width: `${activeTimer === 'zap' ? progress : 0}%` }}></div>
          </div>
          <p className="text-xs text-slate-300 font-mono min-h-[1.5rem]">{activeTimer === 'zap' ? statusText : 'Ready...'}</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-emerald-500/10 to-indigo-500/10 border border-white/10 p-6 rounded-xl">
        <h4 className="text-xl font-black text-white mb-1 uppercase tracking-wide">Hours Saved per Year</h4>
        <p className="text-xs font-bold text-emerald-400 mb-4 tracking-wide uppercase italic opacity-90">
          (and we're cheaper, too!)
        </p>
        <div className="flex items-center justify-center gap-4 text-slate-300 text-sm">
          <div className="text-center">
            <p className="text-3xl font-mono text-red-500 font-bold">2.8 hrs</p>
            <p>Traditional Apps</p>
          </div>
          <span className="text-3xl text-slate-600">VS</span>
          <div className="text-center">
            <p className="text-3xl font-mono text-emerald-400 font-bold">42 mins</p>
            <p>Zap Expenses</p>
          </div>
        </div>
        <p className="mt-4 text-[10px] text-slate-500 italic">*assuming 500 receipts/year (approx. 2 per working day)</p>
      </div>





    </div>
  );
};

export default EfficiencyTimer;
